import React from 'react'
import { v4 as uuid } from 'uuid'
import { useFormContext } from 'react-hook-form'

import { useAPIConversationStream } from 'api/conversation/useAPIConversation'
import {
  useConversationStore,
  useConversationMessageStore,
  useConversationLandingOnboardingStore,
} from 'stores/conversation'
import { CustomError } from 'utils/customError'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'

import type {
  TConversationMessage,
  TConversationMessageErrorType,
} from 'types/conversation'

const useConversation = () => {
  const containerRef = React.useRef(null)
  const responseRef = React.useRef([])
  const { setValue, watch } = useFormContext()
  const userMessage = watch('userMessage')
  const {
    hasConversationId,
    savedMessages,
    conversationId,
    resetConversationId,
    setConversationId,
    setSavedMessages,
    resetSavedMessages,
  } = useConversationStore()
  const {
    isStreaming,
    messages,
    resetMessages,
    setConnecting,
    setLastError,
    setErrorBoundary,
    setStreaming,
    setMessages,
    removeLastMessage,
  } = useConversationMessageStore()
  const { isLandingOnboardingVariantABC } =
    useConversationLandingOnboardingStore()
  const { SETrackConversationPage } = useSETrackEvent()

  const parentMessage = React.useMemo(() => {
    const messagesWithoutError = Object.values(messages).filter(
      (message) => message.status !== 'error'
    )
    return messagesWithoutError?.[Object.keys(messagesWithoutError).length - 2]
  }, [messages])

  const scrollToBottom = () => {
    containerRef?.current?.scrollTo?.(0, containerRef?.current?.scrollHeight)
  }

  const resetLoadingState = () => {
    setConnecting(false)
    setStreaming(false)
  }

  const { refetch } = useAPIConversationStream({
    onOpen: () => {
      setLastError(undefined)
    },
    onMessage: (msg) => {
      setStreaming(true)

      const { messageId, token } = JSON.parse(msg.data)
      responseRef.current.push(token)

      setMessages({
        [messageId]: {
          messageId,
          type: 'server',
          status: 'loading',
          tokens: responseRef.current,
        },
      })

      setTimeout(() => {
        setConnecting(false)
      }, 100)
    },
    onClose: () => {
      responseRef.current = []
      resetLoadingState()
    },
    onError: (err) => {
      const messageId = uuid()
      const { name, message } = err ?? {}

      setLastError(name as TConversationMessageErrorType)
      setMessages({
        [messageId]: {
          messageId,
          type: 'server',
          status: 'error',
          errorType: name as TConversationMessageErrorType,
          message,
        },
      })
      responseRef.current = []
      resetLoadingState()
      throw err
    },
  })

  const startOrResumeSession = () => {
    if (hasConversationId()) {
      return conversationId
    }

    const newConversationId = uuid()
    setConversationId(newConversationId)
    return newConversationId
  }

  const resetSession = () => {
    resetConversationId()
    resetMessages()
    resetUserMessage()
    resetLoadingState()
    resetSavedMessages()
    setLastError(undefined)
  }

  const resetUserMessage = () => {
    setValue('userMessage', '', {
      shouldValidate: true,
    })
  }

  const sendMessage = async (
    message?: string,
    index?: number,
    isFromSuggestion?: boolean
  ) => {
    try {
      responseRef.current = []
      const messageId = uuid()
      const conversationId = startOrResumeSession()
      const messageContent = message ?? userMessage

      setConnecting(true)
      setMessages({
        [messageId]: {
          messageId,
          type: 'client',
          tokens: [messageContent],
        },
      })

      // track click on Suggestions
      if (isFromSuggestion) {
        SETrackConversationPage('INPUT_GURU_AI_PROMPT', {
          is_new_ai_chat_session: [true, 'bool'],
          ai_chat_session_id: conversationId,
          page_element: 'AI_PROMPT_EXAMPLE',
          text_input: messageContent,
          index: [index + 1, 'int'],
        })
      }

      // set Input Box to empty
      resetUserMessage()

      await refetch({
        conversationId,
        messageContent,
        parentMessageId: parentMessage?.messageId,
      })
    } catch (error) {
      // TODO: Handle errors
      if (error instanceof CustomError) {
        setErrorBoundary(error)
      }
    } finally {
      resetLoadingState()
    }
  }

  const retrySendMessage = async () => {
    try {
      setConnecting(true)
      const conversationId = startOrResumeSession()
      const clientMessages = Object.values(messages).filter(
        (msg) => msg.type === 'client'
      )
      const lastClientMessage =
        clientMessages?.pop?.() ?? ({} as TConversationMessage)
      const messageContent = lastClientMessage?.tokens?.[0] ?? ''
      removeLastMessage()

      await refetch({
        conversationId,
        messageContent,
        parentMessageId: parentMessage?.messageId,
      })
    } catch (error) {
      // TODO: Handle errors
      if (error instanceof CustomError) {
        setErrorBoundary(error)
      }
    } finally {
      resetLoadingState()
    }
  }

  React.useEffect(() => {
    const messagesWithoutError = Object.values(messages)
      .filter((message) => message.status !== 'error')
      .reduce(
        (prev, message) => ({
          ...prev,
          [message.messageId]: messages[message.messageId],
        }),
        {}
      )
    const messagesLength = Object.keys(messagesWithoutError).length
    const savedMessagesLength = Object.keys(savedMessages).length
    const lastMessage =
      messagesWithoutError[
        Object.keys(messagesWithoutError)[messagesLength - 1]
      ]

    if (
      !isStreaming &&
      messagesLength !== savedMessagesLength &&
      lastMessage?.type === 'server'
    ) {
      let newData = { ...messagesWithoutError }
      delete newData[lastMessage?.messageId]
      setSavedMessages({
        ...newData,
        [lastMessage?.messageId]: {
          ...lastMessage,
          status: 'done',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, messages])

  React.useEffect(() => {
    if (Object.keys(messages).length > 0 || isLandingOnboardingVariantABC()) {
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingOnboardingVariantABC, messages])

  return {
    sendMessage,
    resetSession,
    resetUserMessage,
    retrySendMessage,
    scrollToBottom,
    containerRef,
  }
}

export default useConversation
